/**
 * Customer Module Documentation
 */

export const customerDoc = {
  title: 'Customer Module',
  content: `# Customer Module Training Guide

## 1. Overview

The **Customer Module** manages all customer (account) information in Engraveca. Customers are the companies or individuals you do business with. Each customer can have multiple contacts, orders, and invoices associated with them.

**Key Concept:** A "Customer" is the business/account level entity. "Contacts" are the individual people associated with a customer.

---

## 2. Customer Data Model

\`\`\`mermaid
flowchart TB
    subgraph Customer["🏢 Customer Account"]
        INFO[Company Info]
        ADDR[Address]
        TAX[Tax Settings]
    end
    
    Customer --> C1[👤 Contact 1]
    Customer --> C2[👤 Contact 2]
    Customer --> C3[👤 Contact 3]
    
    Customer --> O1[📦 Order 1]
    Customer --> O2[📦 Order 2]
    
    O1 --> I1[💵 Invoice 1]
    O2 --> I2[💵 Invoice 2]
    
    style Customer fill:#8B7355,color:#fff
    style C1 fill:#e8d5c4
    style C2 fill:#e8d5c4
    style C3 fill:#e8d5c4
    style O1 fill:#93c5fd
    style O2 fill:#93c5fd
    style I1 fill:#86efac
    style I2 fill:#86efac
\`\`\`

---

## 3. Accessing the Customer Module

**Navigation:** Admin Panel → Order Management → **Customers**

---

## 4. Customer List View

### 4.1 Screen Layout

The Customer list displays all accounts with the following columns:

| Column | Description |
|--------|-------------|
| **Company** | Customer/account name |
| **Phone** | Primary phone number |
| **Website** | Company website URL |
| **Location** | City, State |
| **Tax Exempt** | ✓ if tax exempt |
| **Created** | Account creation date |

### 4.2 Actions Available

- **Search:** Search by company name, phone, or email
- **Filter:** Filter by status (All Statuses dropdown)
- **View:** Click the eye icon (👁) to view customer details
- **Create New:** Click "+ Create New" button to add a new customer

---

## 5. Field Reference

### 5.1 Basic Information

| Field | Required | Description |
|-------|----------|-------------|
| **Company Name** | Yes | Official business name |
| **Phone** | No | Primary contact phone |
| **Website** | No | Company website URL |
| **Email** | No | Primary email address |

### 5.2 Address Information

| Field | Required | Description |
|-------|----------|-------------|
| **Street Address** | No | Street number and name |
| **City** | No | City name |
| **State** | No | State/Province |
| **Zip Code** | No | Postal code |
| **Country** | No | Country (defaults to USA) |

### 5.3 Billing Settings

| Field | Required | Description |
|-------|----------|-------------|
| **Tax Exempt** | No | Check if customer is tax exempt |
| **Tax ID** | No | Tax exemption ID number |
| **Net Terms** | No | Payment terms (Net 7, Net 15, Net 30) |
| **Credit Limit** | No | Maximum credit allowed |

---

## 6. Customer Lifecycle

\`\`\`mermaid
flowchart LR
    subgraph Create["1️⃣ Create"]
        NEW[New Customer]
        INFO[Add Info]
    end
    
    subgraph Engage["2️⃣ Engage"]
        QUOTE[Create Quote]
        ORDER[Place Order]
    end
    
    subgraph Invoice["3️⃣ Bill"]
        INV[Issue Invoice]
        SEND[Send Invoice]
    end
    
    subgraph Collect["4️⃣ Collect"]
        PAY[Record Payment]
        DONE[Complete]
    end
    
    NEW --> INFO --> QUOTE --> ORDER --> INV --> SEND --> PAY --> DONE
    
    style NEW fill:#fef08a
    style ORDER fill:#93c5fd
    style INV fill:#c4b5fd
    style DONE fill:#86efac
\`\`\`

---

## 7. Workflows

### 7.1 Creating a New Customer

**Steps:**

1. Click **"+ Create New"** button
2. Select **"Customer"** from the dropdown
3. Fill in the **Company Name** (required)
4. Add phone and email for communication
5. Enter billing address
6. Set tax exemption status if applicable
7. Set payment terms (Net Terms)
8. Click **"Save"**

**Best Practice:** Always capture the customer's primary contact information when creating a new account.

### 7.2 Customer → Order Flow

\`\`\`mermaid
flowchart LR
    A[Select Customer] --> B{Has Quote?}
    B -->|Yes| C[Review Quote]
    B -->|No| D[Create Order]
    C --> E[Convert to Order]
    E --> D
    D --> F[Issue Invoice]
    F --> G[Receive Payment]
    
    style A fill:#e8d5c4
    style D fill:#93c5fd
    style F fill:#c4b5fd
    style G fill:#86efac
\`\`\`

1. **Create or Select Customer** - Start with the customer account
2. **Create Quote** - Optional: Generate a quote for the customer
3. **Convert to Order** - Accept the quote or create an order directly
4. **Issue Invoice** - Generate invoice for payment
5. **Receive Payment** - Record payment against the invoice

---

## 8. Tips & Best Practices

### Do's ✅

- Always verify customer information before creating orders
- Set appropriate Net Terms based on customer relationship
- Mark tax-exempt customers and keep tax IDs updated
- Add multiple contacts for larger companies
- Use the search function to avoid duplicate entries

### Don'ts ❌

- Don't create duplicate customer records for the same company
- Don't skip required fields when creating new customers
- Don't change billing address after invoices have been issued
- Don't delete customers with active orders

---

## 9. Common Issues & Solutions

| Issue | Solution |
|-------|----------|
| Can't find a customer | Use search bar with partial company name or email |
| Customer appears twice | Merge duplicate records (contact admin) |
| Tax showing incorrectly | Check Tax Exempt checkbox and Tax ID |
| Wrong billing address | Edit customer record before issuing new invoice |

---

*Last Updated: February 2026*
`
};
